import { computed, Injectable, signal } from "@angular/core";
import { StreappEvent as FullEvent } from 'src/app/models/streappevent.model';
import { StreappEventStep } from "../../_models/streapp-event-step.model";
import { EventType, EventTypeMain } from "../../_models/eventtype.model";
import moment from "moment";
import { EventFormStore } from "../../_services/event-form-store.service";

@Injectable()
export class CreateEventFormStore extends EventFormStore {
    
    public stepTrackers = [
        this.stepMediaItemsUploadValid,
        this.stepLocationValid,
        this.stepDetailsValid,
        this.stepCategoryValid,
        this.stepPrivacyValid,
        this.stepPriceValid,
    ]

    public steps = computed<Array<StreappEventStep>>(() => {
        return [
            { title: 'step_media_upload', letter: 'S', state: this.currentStep() === 0 ? 'active' : (this.stepMediaItemsUploadValid() ? 'completed' : 'default'), disabled: false },
            { title: 'step_location', letter: 'T', state: this.currentStep() === 1 ? 'active' : (this.stepLocationValid() ? 'completed' : 'default'), disabled: false },
            { title: 'step_details', letter: 'R', state: this.currentStep() === 2 ? 'active' : (this.stepDetailsValid() ? 'completed' : 'default'), disabled: false },
            { title: 'step_category', letter: 'E', state: this.currentStep() === 3 ? 'active' : (this.stepCategoryValid() ? 'completed' : 'default'), disabled: false },
            { title: 'step_privacy', letter: 'A', state: this.currentStep() === 4 ? 'active' : (this.stepPrivacyValid() ? 'completed' : 'default'), disabled: false },
            { title: 'step_price', letter: 'P', state: this.currentStep() === 5 ? 'active' : (this.stepPriceValid() ? 'completed' : 'default'), disabled: this.streappEvent().preUploadedMediaItems.length === 0 },
            { title: 'step_publish', letter: 'P', state: this.currentStep() === 6 ? 'active' : 'default', disabled: false }
        ];
    });

    public readonly canGoNext = computed(() => {
        if (this.isLoading()) {
            return false;
        }

        return this.isStepCompleted(this.currentStep())
    });

    public readonly isValid = computed(() => {
        return this.stepTrackers.every(st => st());
    });

    public linkEvent(event: FullEvent): void {
        const category = this.streappService.eventTypes().find(e => e.title === event.category);
        const subcategory = category?.children?.find(e => e.title === event.subcategory);
        
        this.streappEvent.update(streapp => ({
            ...streapp,
            linkedToMainStreappID: event.partitionKey,
            category: category,
            subcategory: subcategory,
            title: event.title,
            latitude: event.latitude,
            longtitude: event.longtitude,
            location: event.locationFullName,
        }));
    }

    public resetLinkedEvent(): void {
        this.streappEvent.update(streapp => ({
            ...streapp,
            category: undefined,
            subcategory: undefined,
            title: '',
            linkedToMainStreappID: undefined,
        }));
    }

    public updateCategories(category: EventTypeMain, subcategory: EventType): void {
        this.streappEvent.update(streapp => ({
            ...streapp,
            category: category,
            subcategory: subcategory
        }));
    }

    public async setStep(step: number): Promise<void> {
        // Fix real is publishing state check
        if (this.isLoading() || this.isPublishing()) {
            // Prevent navigating while publishing
            return;
        }

        if(this.currentStep() === 4 && step !== 4 && this.privacyStepLeaveGuard) {
            const canLeave = await this.privacyStepLeaveGuard();
            if (!canLeave) {
                return;
            }
        }

        //Prevent clicking on steps that are not completed (except the last available unfinished step)
        if (step > 0 && 
            !this.isStepCompleted(step) && 
            !this.isStepCompleted(step - 1)
        ) {
            return;
        }

        // Price step is completed by just visiting the page
        if (step === 5) {
            this.stepPriceValid.set(true);
        }

        this.currentStep.set(step);
    }

    public reset(): void {
        this.resetEvent();

        this.currentStep.set(0);
        this.stepTrackers.map(t => t.set(false))
        
        this.automaticBlur.set(true);
        this.privacyStepLeaveGuard = null;
        
        this.isLoading.set(false);
        this.error.set(null);
        this.eventID.set(undefined);
    }

    public async createEvent(): Promise<string> {
        this.isLoading.set(true);
        this.error.set(null);

        const eventData = this.setEventData();

        try {
            const eventId = await this.streappService.initStreappEvent(JSON.stringify(eventData), this.authService.authorizationHeaderValue);

            await Promise.all([
                this.uploadService.uploadFiles(eventId, this.preUploadedMediaItems(), false, this.streappEvent().privacyEnabled, (index, patch) => this.updateProgress(index, patch.progress!, patch.finished!, false)),
                this.uploadService.uploadFiles(eventId, this.preUploadedShopMediaItems(), true, this.streappEvent().privacyEnabled, (index, patch) => this.updateProgress(index, patch.progress!, patch.finished!, true)),
            ]);

            await this.streappService.completeStreappEvent(
                JSON.stringify({
                    ID: eventId,
                }),
                this.authService.authorizationHeaderValue
            );

            return eventId;
        }
        catch (error) {
            this.error.set(this.getErrorMessage(error));
            throw error;
        } finally {
            this.isLoading.set(false);
        }
    }

    private setEventData(): any {
        const created = moment(this.streappEvent().created)
            .utcOffset(new Date().getTimezoneOffset())
            .toDate();
        return {
            ID: this.eventID(),
            Title: this.streappEvent().title,
            Description: this.streappEvent().description,
            Category: this.streappEvent().category?.title,
            Subcategory: this.streappEvent().subcategory?.title,
            InjuriesPresent: this.streappEvent().injuriesPresent ?? 'onbekend',
            EmergencyServicesPresent: this.streappEvent().emergencyServicesPresent ?? false,
            ShockingEvent: this.streappEvent().highImpactEvent,
            Tags: this.streappEvent().tags.join(','),
            Latitude: this.streappEvent().latitude,
            Longtitude: this.streappEvent().longtitude,
            LinkedToMainStreappID: this.streappEvent().linkedToMainStreappID,
            Created: created,
        };
    }

    private isStepCompleted(index: number): boolean {
        const event = this.streappEvent();

        switch (index) {
            case 0:
                return event.preUploadedMediaItems.length > 0;
            case 1:
                return event.latitude !== 0 && event.longtitude !== 0;
            case 2:
                return !!(event.linkedToMainStreappID && event.description) || !!(event.title && event.description);
            case 3:
                return !!(event.category && event.subcategory);
            case 4:
                return this.stepPrivacyValid();
            case 5:
                return this.stepPriceValid();
            default:
                return false;
        }
    }
}